/**
 * Shared team display name mapping and logo utilities
 *
 * Imports team data from shared/teams.json - the single source of truth
 * for team names across both frontend and backend.
 */

import teamsData from '../../shared/teams.json'

interface TeamNameConfig {
  formal: string
  informal: string
  short: string
}

// Import team list from shared JSON
const TEAM_NAMES: TeamNameConfig[] = teamsData.teams

// Build lookup maps from the single source of truth
// Maps any variant of a team name to its config
const TEAM_LOOKUP = new Map<string, TeamNameConfig>()

TEAM_NAMES.forEach((team) => {
  // Index by formal name
  TEAM_LOOKUP.set(team.formal.toLowerCase(), team)
  // Index by informal name
  TEAM_LOOKUP.set(team.informal.toLowerCase(), team)
  // Index by short code
  TEAM_LOOKUP.set(team.short.toLowerCase(), team)
})

// Import aliases from shared JSON
const TEAM_ALIASES: Record<string, string> = teamsData.aliases

function findTeamConfig(teamName: string): TeamNameConfig | undefined {
  const lower = teamName.toLowerCase()

  // Direct lookup
  if (TEAM_LOOKUP.has(lower)) {
    return TEAM_LOOKUP.get(lower)
  }

  // Try alias
  const aliasKey = TEAM_ALIASES[lower]
  if (aliasKey && TEAM_LOOKUP.has(aliasKey)) {
    return TEAM_LOOKUP.get(aliasKey)
  }

  return undefined
}

/** Get formal/full team name (e.g., "Manchester City", "West Ham United") */
export function getFormalName(teamName: string): string {
  const config = findTeamConfig(teamName)
  return config?.formal || teamName
}

/** Get display name - maps API names to common names (legacy, use getInformalName for new code) */
export function getDisplayName(teamName: string): string {
  const config = findTeamConfig(teamName)
  return config?.informal || teamName
}

/** Get informal/shortened name for display (e.g., "Man City", "Wolves") */
export function getInformalName(teamName: string): string {
  const config = findTeamConfig(teamName)
  return config?.informal || teamName
}

/** Get short 3-letter code (e.g., "MCI", "WHU") */
export function getShortName(teamName: string): string {
  const config = findTeamConfig(teamName)
  return config?.short || teamName.slice(0, 3).toUpperCase()
}

/**
 * Normalize a team name to a canonical lowercase form.
 * Resolves aliases so that e.g. "Nott'm Forest" and "Nottingham Forest"
 * both become "nottingham forest".
 */
export function normalizeTeam(name: string | null): string {
  const config = findTeamConfig(name || '')
  return config ? config.formal.toLowerCase() : (name || '').trim().toLowerCase()
}

/**
 * Check if two team names refer to the same team.
 * Handles all variants (formal, informal, short, aliases).
 *
 * Example: isSameTeam("Tottenham Hotspur", "Spurs") => true
 */
export function isSameTeam(name1: string, name2: string): boolean {
  return normalizeTeam(name1) === normalizeTeam(name2)
}

interface TeamInfo {
  name: string
  logo: string
  code?: string
}

export function getTeamLogo(teamName: string, teams?: TeamInfo[]): string {
  if (teams) {
    // Use isSameTeam for robust matching that handles all name variants
    const match = teams.find((t) => isSameTeam(t.name, teamName))
    if (match) return match.logo
  }

  // Fallback emoji logos (keyed by informal name)
  const informalName = getInformalName(teamName)
  const fallback: Record<string, string> = {
    Arsenal: '🔴',
    'Aston Villa': '🟣',
    Bournemouth: '🔴',
    Brentford: '🔴',
    Brighton: '🔵',
    Burnley: '🟣',
    Chelsea: '🔵',
    'Crystal Palace': '🔵',
    Everton: '🔵',
    Fulham: '⚪',
    Leeds: '🟡',
    Liverpool: '🔴',
    'Man City': '🔵',
    'Man Utd': '🔴',
    Newcastle: '⚫',
    "Nott'm Forest": '🔴',
    Sunderland: '🔴',
    Tottenham: '⚪',
    'West Ham': '🟣',
    Wolves: '🟠',
  }
  return fallback[informalName] || '⚽'
}

/**
 * Convert decimal odds to American format
 */
export function formatOdds(decimal: number): string {
  if (decimal >= 2) {
    return `+${Math.round((decimal - 1) * 100)}`
  }
  return `${Math.round(-100 / (decimal - 1))}`
}

/** Export the team list for use elsewhere */
export { TEAM_NAMES }
